import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  toast,
} from "react-toastify";

import {
  fetchFollowingClubs,
  unfollowClient,
} from "../redux/slices/clientFollowSlice";

// ==========================
// API BASE URL
// ==========================
const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// IMAGE / UPLOAD URL HELPER
// ==========================
const getUploadUrl = (
  file?: string | null
) => {
  if (
    !file ||
    String(file).trim() === ""
  ) {
    return "/assets/images/user.png";
  }

  const value =
    String(file).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  if (
    value.startsWith("/uploads/")
  ) {
    return encodeURI(
      `${API_BASE}${value}`
    );
  }

  if (
    value.startsWith("uploads/")
  ) {
    return encodeURI(
      `${API_BASE}/${value}`
    );
  }

  if (
    value.startsWith("/")
  ) {
    return encodeURI(
      `${API_BASE}${value}`
    );
  }

  return encodeURI(
    `${API_BASE}/uploads/${value}`
  );
};

const Contacts: React.FC =
  () => {
    const dispatch =
      useDispatch<any>();

    const {
      followingClubs = [],
      followingLoading,
    } = useSelector(
      (state: any) =>
        state.clientFollow
    );

    // ==========================
    // CURRENT USER
    // ==========================
    const user = JSON.parse(
      localStorage.getItem(
        "user"
      ) || "{}"
    );

    // ==========================
    // CURRENT UNFOLLOWING CLUB
    // ==========================
    const [
      unfollowingClientId,
      setUnfollowingClientId,
    ] = useState<number | null>(
      null
    );

    // ==========================
    // CONFIRM UNFOLLOW MODAL
    // ==========================
    const [
      confirmClub,
      setConfirmClub,
    ] = useState<any | null>(
      null
    );

    // ==========================
    // FETCH FOLLOWING CLUBS
    // ==========================
    const loadFollowingClubs =
      () => {
        if (
          !user?.user_id
        ) {
          return;
        }

        dispatch(
          fetchFollowingClubs({
            user_id:
              user.user_id,

            follower_user_id:
              user.user_id,
          })
        );
      };

    useEffect(() => {
      loadFollowingClubs();
    }, [
      dispatch,
      user?.user_id,
    ]);

    // ==========================
    // UNFOLLOW CLUB
    // ==========================
    const handleUnfollow =
      async (
        club: any
      ) => {
        if (
          !user?.user_id
        ) {
          toast.error(
            "Login required"
          );

          return;
        }

        try {
          setUnfollowingClientId(
            Number(
              club.client_id
            )
          );

          const result =
            await dispatch(
              unfollowClient({
                client_id:
                  club.client_id,

                follower_user_id:
                  user.user_id,

                user_id:
                  user.user_id,
              })
            ).unwrap();

          if (
            result?.status ===
            true
          ) {
            toast.success(
              result?.message ||
                "Club unfollowed successfully"
            );

            setConfirmClub(null);

            loadFollowingClubs();

            return;
          }

          toast.error(
            result?.message ||
              "Failed to unfollow club"
          );
        } catch (
          error: any
        ) {
          console.error(
            "UNFOLLOW CLUB ERROR =>",
            error
          );

          toast.error(
            error?.message ||
              "Failed to unfollow club"
          );
        } finally {
          setUnfollowingClientId(
            null
          );
        }
      };

    return (
      <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 bs-follow-card">
        {/* ==========================
            HEADER
        ========================== */}
        <div className="bs-follow-header">
          <h4>
            Clubs you follow
          </h4>

          <Link
            to="/clubs"
            className="bs-follow-seeall"
          >
            See all

            <i className="feather-arrow-right" />
          </Link>
        </div>

        {/* ==========================
            LOADING
        ========================== */}
        {followingLoading && (
          <div className="bs-follow-skeleton-wrap">
            {Array.from({
              length: 3,
            }).map(
              (_, i) => (
                <div
                  className="bs-follow-skeleton-row"
                  key={i}
                >
                  <div className="bs-follow-skeleton-avatar" />

                  <div className="bs-follow-skeleton-text">
                    <div className="bs-follow-skeleton-line bs-follow-skeleton-line--wide" />

                    <div className="bs-follow-skeleton-line bs-follow-skeleton-line--narrow" />
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {/* ==========================
            EMPTY STATE
        ========================== */}
        {!followingLoading &&
          followingClubs.length ===
            0 && (
            <div className="bs-follow-empty">
              <div className="bs-follow-empty-icon">
                <i className="feather-users" />
              </div>

              <p>
                You aren't following
                any clubs yet
              </p>

              <Link
                to="/clubs"
                className="bs-follow-empty-link"
              >
                Discover clubs
              </Link>
            </div>
          )}

        {/* ==========================
            FOLLOWING CLUBS
        ========================== */}
        {!followingLoading &&
          followingClubs.length >
            0 && (
            <div className="bs-follow-scroll">
              {followingClubs.map(
                (
                  club: any,
                  index: number
                ) => {
                  const logo =
                    getUploadUrl(
                      club.logo_image
                    );

                  const isUnfollowing =
                    Number(
                      unfollowingClientId
                    ) ===
                    Number(
                      club.client_id
                    );

                  const clubUrl =
                    club.unique_slug
                      ? `/club/${club.unique_slug}`
                      : `/club/${club.client_id}`;

                  return (
                    <div
                      key={
                        club.client_id ||
                        index
                      }
                      className="bs-follow-item"
                    >
                      {/* CLUB LOGO */}
                      <figure className="bs-follow-avatar-wrap">
                        <img
                          src={logo}
                          alt={
                            club.client_name ||
                            "Club"
                          }
                          onError={(
                            event
                          ) => {
                            event.currentTarget.src =
                              "/assets/images/user.png";
                          }}
                        />
                      </figure>

                      {/* CLUB INFO */}
                      <div className="bs-follow-info">
                        <Link
                          to={
                            clubUrl
                          }
                          className="bs-follow-link"
                        >
                          <h4 className="bs-follow-name">
                            {club.client_name ||
                              "Club"}
                          </h4>

                          <span className="bs-follow-level">
                            {club.level_name ||
                              "Club"}
                          </span>
                        </Link>
                      </div>

                      {/* ACTIONS */}
                      <div className="bs-follow-actions">
                        {/* UNFOLLOW */}
                        <button
                          type="button"
                          className="bs-follow-unfollow"
                          disabled={
                            isUnfollowing
                          }
                          onClick={() =>
                            setConfirmClub(
                              club
                            )
                          }
                        >
                          {isUnfollowing
                            ? "Unfollowing..."
                            : "Unfollow"}
                        </button>

                        {/* OPEN CLUB */}
                        <Link
                          to={
                            clubUrl
                          }
                          className="bs-follow-chevron"
                          title="Open club"
                        >
                          <i className="feather-chevron-right" />
                        </Link>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}


        {/* ==========================
            CUSTOM UNFOLLOW CONFIRMATION
        ========================== */}
        {confirmClub && (
          <div
            className="bs-confirm-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                  event.currentTarget &&
                unfollowingClientId ===
                  null
              ) {
                setConfirmClub(null);
              }
            }}
          >
            <div
              className="bs-confirm-box"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bs-unfollow-title"
            >
              <div className="bs-confirm-icon">
                <i className="feather-user-minus" />
              </div>

              <h4 id="bs-unfollow-title">
                Unfollow Club?
              </h4>

              <p>
                Are you sure you want
                to unfollow{" "}
                <strong>
                  {confirmClub.client_name ||
                    "this club"}
                </strong>
                ?
              </p>

              <div className="bs-confirm-actions">
                <button
                  type="button"
                  className="bs-confirm-cancel"
                  disabled={
                    unfollowingClientId !==
                    null
                  }
                  onClick={() =>
                    setConfirmClub(null)
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="bs-confirm-unfollow"
                  disabled={
                    unfollowingClientId !==
                    null
                  }
                  onClick={() =>
                    handleUnfollow(
                      confirmClub
                    )
                  }
                >
                  {unfollowingClientId !==
                  null
                    ? "Unfollowing..."
                    : "Yes, Unfollow"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          /* ==========================================
             FOLLOWING CLUB CARD
          ========================================== */

          .bs-follow-card {
            overflow: hidden;
            border: 1px solid #DCE4F5 !important;
          }

          /* ==========================================
             HEADER
          ========================================== */

          .bs-follow-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 18px 18px 12px;
          }

          .bs-follow-header h4 {
            margin: 0;
            color: #0A2354;
            font-size: 15px;
            font-weight: 700;
          }

          .bs-follow-seeall {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
            color: #2F6FE4;
            font-size: 12px;
            font-weight: 700;
            text-decoration: none;
            transition: 0.15s ease;
          }

          .bs-follow-seeall i {
            font-size: 13px;
            transition:
              transform 0.15s ease;
          }

          .bs-follow-seeall:hover {
            color: #1447B0;
          }

          .bs-follow-seeall:hover i {
            transform:
              translateX(2px);
          }

          /* ==========================================
             CLUB LIST
          ========================================== */

          .bs-follow-scroll {
            max-height: 260px;
            overflow-y: auto;
            padding:
              0 12px 14px;

            scrollbar-width:
              thin;

            scrollbar-color:
              #DCE4F5 transparent;
          }

          .bs-follow-scroll::-webkit-scrollbar {
            width: 6px;
          }

          .bs-follow-scroll::-webkit-scrollbar-track {
            background:
              transparent;
          }

          .bs-follow-scroll::-webkit-scrollbar-thumb {
            background:
              #DCE4F5;
            border-radius:
              999px;
          }

          .bs-follow-scroll::-webkit-scrollbar-thumb:hover {
            background:
              #B9CCF5;
          }

          /* ==========================================
             CLUB ITEM
          ========================================== */

          .bs-follow-item {
            display: flex;
            align-items: center;
            gap: 10px;

            padding: 10px;

            margin-bottom:
              6px;

            border-radius:
              14px;

            transition:
              background
              0.15s ease;
          }

          .bs-follow-item:last-child {
            margin-bottom: 0;
          }

          .bs-follow-item:hover {
            background:
              #F4F7FC;
          }

          /* ==========================================
             CLUB LOGO
          ========================================== */

          .bs-follow-avatar-wrap {
            width: 44px;
            height: 44px;

            flex-shrink: 0;

            margin: 0;
            padding: 2px;

            border-radius:
              50%;

            background:
              linear-gradient(
                135deg,
                #EAF1FF,
                #DCE4F5
              );
          }

          .bs-follow-avatar-wrap img {
            display: block;

            width: 100%;
            height: 100%;

            border:
              2px solid #ffffff;

            border-radius:
              50%;

            object-fit:
              cover;
          }

          /* ==========================================
             CLUB INFORMATION
          ========================================== */

          .bs-follow-info {
            flex: 1;
            min-width: 0;
          }

          .bs-follow-link {
            display: block;
            text-decoration: none;
          }

          .bs-follow-name {
            overflow:
              hidden;

            margin: 0;

            color:
              #0A2354;

            font-size:
              13.5px;

            font-weight:
              700;

            white-space:
              nowrap;

            text-overflow:
              ellipsis;
          }

          .bs-follow-level {
            display:
              block;

            overflow:
              hidden;

            margin-top:
              2px;

            color:
              #8A93A3;

            font-size:
              11.5px;

            font-weight:
              500;

            white-space:
              nowrap;

            text-overflow:
              ellipsis;
          }

          /* ==========================================
             ACTIONS
          ========================================== */

          .bs-follow-actions {
            display:
              flex;

            align-items:
              center;

            gap: 6px;

            flex-shrink:
              0;
          }

          /* ==========================================
             UNFOLLOW BUTTON
          ========================================== */

          .bs-follow-unfollow {
            display:
              inline-flex;

            align-items:
              center;

            justify-content:
              center;

            height: 30px;

            padding:
              0 12px;

            flex-shrink:
              0;

            border:
              1px solid
              #F2C4C8;

            border-radius:
              999px;

            background:
              #ffffff;

            color:
              #DC3545;

            font-size:
              11px;

            font-weight:
              700;

            white-space:
              nowrap;

            cursor:
              pointer;

            transition:
              0.15s ease;
          }

          .bs-follow-unfollow:hover:not(:disabled) {
            border-color:
              #DC3545;

            background:
              #DC3545;

            color:
              #ffffff;
          }

          .bs-follow-unfollow:disabled {
            opacity:
              0.55;

            cursor:
              not-allowed;
          }

          /* ==========================================
             CHEVRON
          ========================================== */

          .bs-follow-chevron {
            display:
              flex;

            align-items:
              center;

            justify-content:
              center;

            width: 30px;
            height: 30px;

            flex-shrink:
              0;

            border:
              1px solid
              #DCE4F5;

            border-radius:
              50%;

            background:
              #ffffff;

            color:
              #0A2354;

            text-decoration:
              none;

            transition:
              0.15s ease;
          }

          .bs-follow-chevron:hover {
            border-color:
              #2F6FE4;

            background:
              #2F6FE4;

            color:
              #ffffff;
          }

          .bs-follow-chevron i {
            font-size:
              13px;
          }

          /* ==========================================
             CUSTOM CONFIRMATION MODAL
          ========================================== */

          .bs-confirm-overlay {
            position:
              fixed;

            inset: 0;

            z-index:
              99999;

            display:
              flex;

            align-items:
              center;

            justify-content:
              center;

            padding:
              20px;

            background:
              rgba(
                6,
                23,
                51,
                0.48
              );

            backdrop-filter:
              blur(3px);
          }

          .bs-confirm-box {
            width:
              100%;

            max-width:
              360px;

            padding:
              24px;

            border:
              1px solid
              #E7ECF5;

            border-radius:
              20px;

            background:
              #ffffff;

            box-shadow:
              0 24px 70px
              rgba(
                6,
                23,
                51,
                0.22
              );

            text-align:
              center;

            animation:
              bs-confirm-pop
              0.18s
              ease-out;
          }

          @keyframes bs-confirm-pop {
            from {
              opacity: 0;
              transform:
                scale(0.96)
                translateY(4px);
            }

            to {
              opacity: 1;
              transform:
                scale(1)
                translateY(0);
            }
          }

          .bs-confirm-icon {
            display:
              inline-flex;

            align-items:
              center;

            justify-content:
              center;

            width: 50px;
            height: 50px;

            margin-bottom:
              12px;

            border-radius:
              50%;

            background:
              #FFF1F2;

            color:
              #DC3545;

            font-size:
              20px;
          }

          .bs-confirm-box h4 {
            margin:
              0 0 8px;

            color:
              #0A2354;

            font-size:
              18px;

            font-weight:
              700;
          }

          .bs-confirm-box p {
            margin: 0;

            color:
              #6B7280;

            font-size:
              13px;

            line-height:
              1.6;
          }

          .bs-confirm-box p strong {
            color:
              #0A2354;

            font-weight:
              700;
          }

          .bs-confirm-actions {
            display:
              flex;

            gap:
              10px;

            margin-top:
              22px;
          }

          .bs-confirm-cancel,
          .bs-confirm-unfollow {
            flex: 1;

            height:
              40px;

            border-radius:
              999px;

            font-size:
              12.5px;

            font-weight:
              700;

            cursor:
              pointer;

            transition:
              0.15s ease;
          }

          .bs-confirm-cancel {
            border:
              1px solid
              #DCE4F5;

            background:
              #ffffff;

            color:
              #0A2354;
          }

          .bs-confirm-cancel:hover:not(:disabled) {
            background:
              #F4F7FC;
          }

          .bs-confirm-unfollow {
            border:
              1px solid
              #DC3545;

            background:
              #DC3545;

            color:
              #ffffff;
          }

          .bs-confirm-unfollow:hover:not(:disabled) {
            background:
              #B42332;

            border-color:
              #B42332;
          }

          .bs-confirm-cancel:disabled,
          .bs-confirm-unfollow:disabled {
            opacity:
              0.6;

            cursor:
              not-allowed;
          }

          /* ==========================================
             LOADING SKELETON
          ========================================== */

          .bs-follow-skeleton-wrap {
            padding:
              0 12px 14px;
          }

          .bs-follow-skeleton-row {
            display:
              flex;

            align-items:
              center;

            gap: 12px;

            padding:
              10px;
          }

          .bs-follow-skeleton-avatar {
            width: 44px;
            height: 44px;

            flex-shrink:
              0;

            border-radius:
              50%;

            background:
              linear-gradient(
                90deg,
                #EAF1FF 25%,
                #F4F7FC 37%,
                #EAF1FF 63%
              );

            background-size:
              400% 100%;

            animation:
              bs-follow-shimmer
              1.4s ease
              infinite;
          }

          .bs-follow-skeleton-text {
            flex: 1;
          }

          .bs-follow-skeleton-line {
            height: 9px;

            border-radius:
              5px;

            background:
              linear-gradient(
                90deg,
                #EAF1FF 25%,
                #F4F7FC 37%,
                #EAF1FF 63%
              );

            background-size:
              400% 100%;

            animation:
              bs-follow-shimmer
              1.4s ease
              infinite;
          }

          .bs-follow-skeleton-line--wide {
            width: 65%;
          }

          .bs-follow-skeleton-line--narrow {
            width: 35%;
            margin-top: 7px;
          }

          @keyframes bs-follow-shimmer {
            0% {
              background-position:
                100% 0;
            }

            100% {
              background-position:
                -100% 0;
            }
          }

          /* ==========================================
             EMPTY STATE
          ========================================== */

          .bs-follow-empty {
            display:
              flex;

            flex-direction:
              column;

            align-items:
              center;

            padding:
              20px 18px
              26px;

            text-align:
              center;
          }

          .bs-follow-empty-icon {
            display:
              flex;

            align-items:
              center;

            justify-content:
              center;

            width: 48px;
            height: 48px;

            margin-bottom:
              10px;

            border-radius:
              14px;

            background:
              rgba(
                47,
                111,
                228,
                0.10
              );

            color:
              #2F6FE4;

            font-size:
              20px;
          }

          .bs-follow-empty p {
            margin: 0;

            color:
              #8A93A3;

            font-size:
              12.5px;

            font-weight:
              500;
          }

          .bs-follow-empty-link {
            margin-top:
              8px;

            color:
              #2F6FE4;

            font-size:
              12.5px;

            font-weight:
              700;

            text-decoration:
              none;
          }

          .bs-follow-empty-link:hover {
            color:
              #1447B0;
          }
        `}</style>
      </div>
    );
  };

export default Contacts;